'use client';

import { faLocationDot } from '@fortawesome/pro-light-svg-icons';
import { useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { useStore } from 'zustand';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { LocationModalContent } from './location-modal-content.component';

import { useRecentLocationSearches } from '@/app/(main)/(pages)/(home)/_components/location/_hooks/use-recent-location-searches.hook';
import { useLocationSelection } from '@/app/(main)/(pages)/(home)/_components/location/_hooks/use-location-selection.hook';
import { useLocationSearch } from '@/app/(main)/(pages)/(home)/_components/location/_hooks/use-location-search.hook';
import { Modal } from '@/components/ui/modal.component';
import { authStore } from '@/store/auth.store';
import { modalsOpeningStore, setDeniedGeolocation, setOpenLocationSelectionModal } from '@/store/modal';
import { resetUserLocation, userLocationStore } from '@/store/user-location.store';
import { getUserLocationStorage } from '@/storage/location.storage';
import { formatLocationLabel } from '@/utils/formatters/location/format-location-label.util';
import { Place } from '@/types/google-maps.types';
import useIsMobile from '@/hooks/use-is-mobile.hook';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Icon } from '@/components/ui/icon.component';
import { ModalCloseButton } from '@/components/ui/modal-close-button/modal-close-button.component';
import { TrackUserView } from '@/app/(main)/_components/track-user-view/track-user-view.component';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import { useSearchParams } from '@/hooks/use-search-params.hook';
import { PersistedUserNavigationSearchParams } from '@/types/enums/persisted-user-navigation-search-params.enum';

const CITY_SEARCH_PARAM_KEYS = [PersistedUserNavigationSearchParams.CIDADE] as const;

export const LocationSelectionModal = () => {
  const isMobile = useIsMobile();

  const isLoggedIn = useStore(authStore, (state) => state.computed.isLoggedIn);
  const authStatus = useStore(authStore, (state) => state.authStatus);
  const { recentSearches } = useRecentLocationSearches();
  const { open, deniedGeolocation } = useStore(modalsOpeningStore, (state) => ({
    open: state?.locationSelectionModalIsOpen,
    deniedGeolocation: state?.deniedGeolocation,
  }));

  const {
    searchDisplayValue,
    searchResults,
    isLoading,
    shouldShowEmptyResultsMessage,
    hasSearchError,
    handleSearchInput,
    clearSearch,
    resetSearchResults,
  } = useLocationSearch();

  const {
    hasSelectionError,
    hasGeolocationError,
    isGeolocating,
    clearSelectionError,
    cancelGeolocation,
    handleMyLocation,
    handleSearchResultClick,
    handleRegisterAddressClick,
    handleSuggestionClick,
  } = useLocationSelection();

  const userLocation = useStore(userLocationStore, (state) => state?.userLocation);
  const [storedLocation, setStoredLocation] = useState<Place | null>(null);
  const { cidade: selectedCity } = useSearchParams(CITY_SEARCH_PARAM_KEYS);
  const pathname = usePathname();

  const currentLocationLabel = selectedCity
    ? formatLocationLabel(userLocation ?? storedLocation ?? undefined)
    : '';

  const prevAuthStatusRef = useRef(authStatus);
  const prevPathnameRef = useRef(pathname);

  useEffect(() => {
    if (prevPathnameRef.current === pathname) {
      return;
    }

    prevPathnameRef.current = pathname;
    setOpenLocationSelectionModal(false);
  }, [pathname]);

  useEffect(() => {
    const prevAuthStatus = prevAuthStatusRef.current;
    prevAuthStatusRef.current = authStatus;

    const isLoginOrLogout = prevAuthStatus !== authStatus
      && prevAuthStatus !== 'loading'
      && authStatus !== 'loading';

    if (isLoginOrLogout) {
      clearSearch();
      clearSelectionError();
      resetUserLocation();
      setStoredLocation(null);
    }
  }, [authStatus, clearSearch, clearSelectionError]);

  useEffect(() => {
    if (!open) {
      clearSearch();
      clearSelectionError();
      setDeniedGeolocation(false);
      return;
    }

    setStoredLocation(getUserLocationStorage());
  }, [open, clearSearch, clearSelectionError]);

  const handleOpenChange = (isOpen: boolean): void => {
    if (!isOpen) {
      cancelGeolocation();
    }

    setOpenLocationSelectionModal(isOpen);
  };

  const handleCurrentLocationClick = (): void => {
    handleOpenChange(false);
  };

  const handleLocationSearchInput = (event: React.ChangeEvent<HTMLInputElement>): void => {
    clearSelectionError();
    handleSearchInput(event);
  };

  const sharedContentProps = {
    searchDisplayValue,
    searchResults,
    isLoading,
    shouldShowEmptyResultsMessage,
    hasLocationError: hasSearchError || hasSelectionError,
    hasGeolocationError,
    isGeolocating,
    deniedGeolocation,
    isLoggedIn,
    recentSearchesCount: recentSearches.length,
    currentLocationLabel,
    onCurrentLocationClick: handleCurrentLocationClick,
    onSearchInput: handleLocationSearchInput,
    onUseMyLocation: handleMyLocation,
    onLocationClick: handleSearchResultClick,
    onRegisterAddressClick: handleRegisterAddressClick,
    onSuggestionClick: handleSuggestionClick,
    onResetSearchResults: resetSearchResults,
  };

  return (
    <>
      {open && <TrackUserView eventName={AnalyticsEnum.VIEW_LOCATION_MODAL} />}

      {isMobile
        ? (
            <Sheet open={open} onOpenChange={handleOpenChange}>
              <SheetContent
                side="bottom"
                closingTagPosition="right"
                className="w-full p-0 h-full"
                hasHeader={true}
              >
                <SheetHeader>
                  <div className="flex justify-between gap-[10px] p-6 px-5 pb-0 items-start">
                    <SheetTitle>
                      <div className="flex gap-2">
                        <Icon
                          icon={faLocationDot}
                          size="xl"
                          className="text-green-400 mt-1"
                        />
                        <Typography variant={TypographyVariant.MODAL_TITLE}>
                          Qual cidade será o atendimento?
                        </Typography>
                      </div>
                    </SheetTitle>
                    <SheetClose asChild>
                      <ModalCloseButton
                        skipInitialFocus
                        data-testid={DataTestIdEnum.LOCATION_SELECTION_MODAL_CLOSE_BUTTON}
                        className="mt-1"
                      />
                    </SheetClose>
                  </div>
                </SheetHeader>
                <LocationModalContent {...sharedContentProps} />
              </SheetContent>
            </Sheet>
          )
        : (
            <Modal open={open} onOpenChange={handleOpenChange}>
              <Modal.Content
                title="Qual cidade será o atendimento?"
                className="bg-white md:max-w-[37.5rem] md:min-h-[32rem]"
                icon={faLocationDot}
                iconStyle="text-green-400"
              >
                <LocationModalContent {...sharedContentProps} />
              </Modal.Content>
            </Modal>
          )}
    </>
  );
};
